import {ApolloLink} from '@apollo/client';
import type {unstable_InternalOperation as InternalOperation} from '@sail/data';
import {Headers} from '@sail/data';

import addContextHeaders from '../../../shared/addContextHeaders';

import type {V1ContextInput} from '../../../../schema/types';

export default function createContextHeadersLink() {
  return new ApolloLink(function contextHeadersLink(
    operation: InternalOperation,
    forward,
  ) {
    const v1Context = getV1Context(operation.variables);

    operation.setContext((prevContext) => {
      const prevHeaders: {[key: string]: string} = prevContext?.headers ?? {};

      const headers = new Headers([...Object.entries(prevHeaders)]);

      addContextHeaders(headers, v1Context);

      return {
        ...prevContext,
        headers: headers.getFetchHeaders(),
      };
    });

    return forward(operation);
  });
}

function getV1Context(
  variables: InternalOperation['variables'],
): V1ContextInput {
  const accountIdsSet = new Set();
  const apiKeysSet = new Set();
  const betaHeadersSet = new Set();
  const compartmentIdsSet = new Set();
  const liveModesSet = new Set();

  let v1Context: V1ContextInput | null = null;

  Object.entries(variables).forEach(([key, value]) => {
    if (
      key.endsWith('v1Context') &&
      typeof value === 'object' &&
      value !== null
    ) {
      v1Context = value;

      accountIdsSet.add(value.accountId);
      apiKeysSet.add(value.apiKey);
      betaHeadersSet.add(JSON.stringify(value.betaHeaders));
      compartmentIdsSet.add(value.compartmentId);
      liveModesSet.add(value.liveMode);
    }
  });

  // TODO Split request into many if more than one value of accountId/liveMode etc.
  const checkAndWarn = (set: Set<any>, name: string) => {
    if (set.size > 1) {
      console.warn(`Warning: Multiple ${name} found in variables`);
    }
  };

  checkAndWarn(accountIdsSet, 'account IDs');
  checkAndWarn(apiKeysSet, 'API keys');
  checkAndWarn(betaHeadersSet, 'beta headers');
  checkAndWarn(compartmentIdsSet, 'compartment IDs');
  checkAndWarn(liveModesSet, 'live modes');

  if (!v1Context) {
    throw new Error('V1 context not found');
  }

  return v1Context;
}
